<template>
  <ui-modal-layout :title="tr('Post details')" :icon="FileTextIcon">
    <template #toolbar>
      <form-step-tab :items="tabs" equal-width />
    </template>

    <template v-if="isLoading">
      <inline-social-content :entity="null" />
      <div class="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
        <div v-for="i in 3" :key="'metric-skel-' + i" class="glass-tile flex flex-col gap-2 rounded-xl p-3">
          <ui-skeleton class="h-3 w-3/5" />
          <ui-skeleton class="h-6 w-4/5" />
        </div>
      </div>
      <div class="flex flex-col gap-2">
        <ui-skeleton v-for="i in 4" :key="'eval-skel-' + i" class="h-4 w-full" />
      </div>
    </template>

    <template v-else-if="isOriginal">
      <div class="flex flex-col gap-3">
        <social-page :source-profile="post.source_profile" :date="post.created_at" />
        <inline-social-content :entity="post" />
        <a
          v-if="post.url"
          :href="post.url"
          target="_blank"
          class="inline-flex w-fit cursor-pointer items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
        >
          <external-link-icon class="size-4" />
          {{ externalPostLabel }}
        </a>
      </div>

      <section class="flex flex-col gap-3">
        <div class="flex items-center justify-between gap-2">
          <h3 class="m-0 text-sm font-semibold text-foreground">{{ tr('Post metrics') }}</h3>
          <ui-dropdown-menu @update:open="(open) => onMetricMenuOpen(open)">
            <ui-dropdown-menu-trigger as-child>
              <ui-button variant="outline" size="xs">
                <settings-2-icon />
                {{ tr('Customize metrics') }}
              </ui-button>
            </ui-dropdown-menu-trigger>
            <ui-dropdown-menu-content align="end" class="max-h-80 w-56 overflow-y-auto">
              <ui-dropdown-menu-checkbox-item
                v-for="field in metricFields"
                :key="field.machine_name"
                :model-value="isSelected(field)"
                @update:model-value="() => toggleVisibleField(field)"
                @select="(event) => event.preventDefault()"
              >
                {{ tr(field.label) }}
              </ui-dropdown-menu-checkbox-item>
            </ui-dropdown-menu-content>
          </ui-dropdown-menu>
        </div>

        <div class="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
          <div v-for="field in metricVisibleFields" :key="field.machine_name" class="glass-tile flex flex-col gap-1 rounded-xl p-3">
            <span class="text-xs font-semibold text-muted-foreground">{{ tr(field.label) }}</span>
            <span class="text-lg font-bold text-foreground tabular-nums">
              {{ formatNumber(post.metric?.[field.machine_name], field.machine_name === 'ctr' ? '%' : '') }}
            </span>
          </div>
        </div>

        <p class="m-0 text-xs text-muted-foreground">
          {{ tr('Data last updated at :time', { ':time': timeAgo(post.source_profile.fetched_at, tr) }) }}
        </p>
      </section>

      <info-text
        v-if="!post.source_profile.has_active_start_rule && (!post.ai_meta || post.ai_meta.length === 0)"
      >
        {{ tr('The post AI evaluation will not occur until the profile that published it has active Start Automation.') }}
      </info-text>

      <section v-if="post.source_profile.has_active_start_rule || post.ai_content_actuality_checked" class="flex flex-col gap-2">
        <div class="flex items-center justify-between gap-2">
          <h3 class="m-0 text-sm font-semibold text-foreground">{{ tr('Actuality of the content') }}</h3>
          <ui-button
            v-if="(!post.ai_evaluation_possible || post.ai_content_actuality_checked) && !contectActualityFormOpen && !$root.loggedInUser.isRestrictedViewer"
            variant="outline"
            size="xs"
            data-slot="edit-content-actuality"
            @click="() => editDateAndTime()"
          >
            <pencil-icon />
            {{ tr('Edit date and time') }}
          </ui-button>
        </div>

        <div v-if="contectActualityFormOpen" class="flex flex-col gap-3">
          <date-time-input v-model="newContentActuality" />
          <div class="flex flex-wrap items-center gap-2">
            <ui-button size="sm" :disabled="isLoadingContantActualitySave" @click="() => updateContentActuality()">
              <loader-circle-icon v-if="isLoadingContantActualitySave" class="animate-spin" />
              {{ tr('Save') }}
            </ui-button>
            <ui-button
              variant="outline"
              size="sm"
              :disabled="isLoadingContantActualitySave"
              @click="() => cancelDateAndTimeEdit()"
            >
              {{ tr('Cancel') }}
            </ui-button>
            <ui-button
              variant="ghost"
              size="sm"
              class="ml-auto text-destructive hover:text-destructive"
              :disabled="isLoadingContantActualitySave"
              @click="() => clearDateAndTime()"
            >
              {{ tr('Clear date and time') }}
            </ui-button>
          </div>
        </div>
        <info-text v-else-if="!post.ai_evaluation_possible">
          {{ tr('The text of the post is too short. AI evaluation is not possible.') }}
        </info-text>
        <p v-else-if="!post.ai_content_actuality_checked" class="m-0 flex items-center gap-2 text-muted-foreground">
          <loader-circle-icon class="size-4 animate-spin" />
          {{ tr('Checking content actuality...') }}
        </p>
        <p v-else-if="post.ai_content_actuality" class="m-0">
          {{ aiContentAtualityText }}:
          <strong class="block">{{ formatToYMD(post.ai_content_actuality) }} {{ formatTime(post.ai_content_actuality) }}</strong>
        </p>
        <p v-else class="m-0">
          {{ contentActualityNotSetText }}
        </p>
      </section>

      <section
        v-if="(post.source_profile.has_active_start_rule || (post.ai_meta && post.ai_meta.length !== 0)) && post.ai_evaluation_possible && !post.ai_meta_error"
        class="flex flex-col gap-3"
      >
        <h3 class="m-0 text-sm font-semibold text-foreground">{{ tr('Content scoring') }}</h3>
        <ai-score :value="post.ai_value" />

        <div v-if="post.ai_meta && post.ai_meta.length !== 0" class="flex flex-col divide-y divide-border">
          <div v-for="(row, key) in post.ai_meta" :key="'ai-meta-' + key" class="flex flex-col gap-1 py-2.5 first:pt-0">
            <strong>{{ tr(row.title) }}: {{ row.value }} / 10</strong>
            <span v-if="row.details" class="text-muted-foreground">{{ row.details_translation || row.details }}</span>
          </div>
        </div>
      </section>
    </template>

    <template v-else-if="isImproved">
      <div
        v-if="post.ai_meta && post.ai_meta.length !== 0 && (!post.improved_copy || post.improved_copy.inProgress)"
        class="flex flex-col items-center gap-3 py-8 text-center"
      >
        <span class="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <sparkles-icon class="size-6" />
        </span>
        <p class="m-0 max-w-sm text-muted-foreground">
          {{ tr('Let the AI rewrite the copy of this post based on its evaluation, to raise its score.') }}
        </p>
        <ui-button :disabled="improvedCopyInProgress" @click="() => improveCopy()">
          <loader-circle-icon v-if="improvedCopyInProgress" class="animate-spin" />
          <sparkles-icon v-else />
          {{ tr('Generate improved copy') }}
        </ui-button>
      </div>
      <info-text v-else-if="!post.ai_meta || post.ai_meta.length === 0">
        {{ tr('Improved copy cannot be generated until AI evaluation is complete. Please check the issues on the Original tab first.') }}
      </info-text>

      <section v-if="post.improved_copy && post.improved_copy.isDone" class="flex flex-col gap-3">
        <h3 class="m-0 text-sm font-semibold text-foreground">{{ tr('Improved copy') }}</h3>
        <p class="m-0 whitespace-pre-line [overflow-wrap:anywhere]">{{ post.improved_copy.improved_text }}</p>
        <ai-score :value="post.improved_copy.value" :label="tr('AI Score') + ':'" />
        <ul class="m-0 flex list-none flex-col gap-1 p-0">
          <li v-for="(row, key) in post.improved_copy.details" :key="'improved-copy-meta-' + key">
            {{ tr(row.title) }}: {{ row.value }} / 10
          </li>
        </ul>
      </section>
    </template>

    <div v-else-if="isAds" class="flex flex-col gap-2">
      <template v-if="!isAdsListEmpty || hasMoreAds">
        <button
          v-for="ad in adsFromPost"
          :key="ad.id"
          type="button"
          data-slot="post-ad"
          :class="[ 'glass-tile flex w-full cursor-pointer flex-col gap-2 rounded-xl p-3 text-left', GLOW_TRANSITION, adGlow(ad) ]"
          @click="() => openAdDetails(ad)"
        >
          <span v-if="ad.created_by_rule" class="flex min-w-0 items-center gap-2 text-sm font-semibold">
            <rule-type-badge :action="ad.created_by_rule.action" compact />
            <span class="truncate">{{ ad.created_by_rule.name }}</span>
          </span>
          <span class="flex items-center gap-3">
            <inline-social-content :entity="ad" :text="ad.name" class="min-w-0 flex-1" />
            <chevron-right-icon class="size-5 shrink-0 text-primary" />
          </span>
        </button>

        <template v-if="hasMoreAds && isLoadingAds">
          <div
            v-for="i in 2"
            :key="'post-ad-loader-skeleton--' + i"
            ref="adsScrollTrigger"
            class="glass-tile flex flex-col gap-2 rounded-xl p-3"
          >
            <ui-skeleton class="h-4 w-40" />
            <inline-social-content />
          </div>
        </template>
      </template>

      <info-text v-else>
        {{ tr('No ads found for this post.') }}
      </info-text>
    </div>
  </ui-modal-layout>
</template>

<script>
import { ChevronRight, ExternalLink, FileText, LoaderCircle, Pencil, Settings2, Sparkles } from '@lucide/vue';
import { markRaw } from 'vue';

import { AI_STATUS } from '@/consts';
import ImprovedCopy from '@/entities/ImprovedCopy.js';
import Post from '@/entities/Post.js';
import ScrollPagerMixin from '@/mixins/ScrollPager.js';
import MagicToolService from '@/services/MagicTool.js';
import PostService from '@/services/Post.js';
import AdDetailsModal from '@/ui/components/AdDetailsModal.vue';
import AiScore from '@/ui/components/AiScore.vue';
import DateTimeInput from '@/ui/components/DateTimeInput.vue';
import FormStepTab from '@/ui/components/FormStepTab.vue';
import InfoText from '@/ui/components/InfoText.vue';
import InlineSocialContent from '@/ui/components/InlineSocialContent.vue';
import RuleTypeBadge from '@/ui/components/RuleTypeBadge.vue';
import SocialPage from '@/ui/components/SocialPage.vue';
import { GLOW_TRANSITION, ruleActionStyle } from '@/ui/utils/ruleAction.js';
import { formatTime, formatToYMD, timeAgo } from '@/utils/date';
import { formatNumber } from '@/utils/number.js';

const ADS_PER_PAGE = 10;

export default {
  name: 'PostModalContent',
  components: {
    AiScore,
    ChevronRightIcon: ChevronRight,
    DateTimeInput,
    ExternalLinkIcon: ExternalLink,
    FormStepTab,
    InfoText,
    InlineSocialContent,
    LoaderCircleIcon: LoaderCircle,
    PencilIcon: Pencil,
    RuleTypeBadge,
    Settings2Icon: Settings2,
    SocialPage,
    SparklesIcon: Sparkles,
  },
  mixins: [ ScrollPagerMixin ],
  data() {
    return {
      GLOW_TRANSITION,
      FileTextIcon: markRaw(FileText),
      isLoading: true,
      postLoadedFromStore: false,
      post: null,
      fetchTimer: null,
      tmpVisibleFields: [],
      newContentActuality: null,
      contectActualityFormOpen: false,
      isLoadingContantActualitySave: false,
      adsFromPost: [],
      isLoadingAds: false,
      adsPage: 0,
      hasMoreAds: true,
    };
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    metricFields() {
      return this.$store.getters['app/postMetrics'];
    },
    improvedCopyInProgress() {
      return this.post && this.post.improved_copy && this.post.improved_copy.inProgress;
    },
    metricVisibleFields() {
      return this.$store.getters['posts/metricVisibleFields'];
    },
    aiContentAtualityText() {
      if (this.post.ai_content_actuality_status === AI_STATUS.MANUAL) {
        return this.tr('Due to a manual edit, the content loses its relevance on the following date');
      }
      return this.tr('According to Infinite∞Ai, the content loses its relevance at the following date');
    },
    contentActualityNotSetText() {
      if (this.post.ai_content_actuality_status === AI_STATUS.MANUAL) {
        return this.tr('The actuality of this content has been manually removed.');
      }
      return this.tr('The Infinite∞Ai did not find any element in the content that would tie its actuality to time.');
    },
    currentTab() {
      return this.$route.params.tab;
    },
    isOriginal() {
      return this.currentTab === 'original';
    },
    isImproved() {
      return this.currentTab === 'improved';
    },
    isAds() {
      return this.currentTab === 'ads';
    },
    tabs() {
      const tabs = [];

      tabs.push({
        label: this.tr('Original Post'),
        link: `/posts/${this.id}/original`,
      });

      tabs.push({
        label: this.tr('Improved Content'),
        link: `/posts/${this.id}/improved`,
      });

      tabs.push({
        label: this.tr('Ads'),
        link: `/posts/${this.id}/ads`,
      });

      return tabs;
    },
    isAdsListEmpty() {
      return this.adsFromPost.length === 0;
    },
    externalPostLabel() {
      switch (this.post.platform) {
        case 'instagram':
          return this.tr('View on Instagram');
        case 'facebook':
          return this.tr('View on Facebook');
        default:
          return this.tr('View on platform');
      }
    },
  },
  watch: {
    currentTab: {
      immediate: true,
      handler(newTab) {
        if (newTab === 'ads') {
          if (this.adsPage === 0) {
            this.loadAds();
          }
          else if (this.hasMoreAds) {
            this.$nextTick(() => {
              this.initScrollPager('adsScrollTrigger', 'loadAds');
            });
          }
        }
        else {
          this.destroyScrollPagerObserver();
        }
      },
    },
  },
  created() {
    this.$store.commit('posts/setPostModalOpened', true);
    this.loadData(true);
  },
  beforeUnmount() {
    this.destroyFetchTimer();
    this.destroyScrollPagerObserver();
    this.$store.commit('posts/setPostModalOpened', false);
  },
  methods: {
    formatNumber,
    formatTime,
    formatToYMD,
    timeAgo,
    adGlow(ad) {
      return ruleActionStyle(ad.created_by_rule?.action).glow;
    },
    openAdDetails(ad) {
      this.$fullscreenModal.open(this, AdDetailsModal, { ad });
    },
    loadData(force) {
      if (!force) {
        this.post = this.$store.getters['posts/list'].find(post => post.id === this.id);
        this.postLoadedFromStore = !!this.post;
        if (this.postLoadedFromStore) {
          this.isLoading = false;
          this.onUpdated();
          return;
        }
      }

      PostService.get(this.id)
        .then(resp => this.post = resp)
        .catch(err => this.error(err))
        .finally(() => {
          this.isLoading = false;
          this.onUpdated();
        });
    },
    loadAds() {
      if (this.isLoadingAds || !this.hasMoreAds) {
        return;
      }
      this.isLoadingAds = true;
      ++this.adsPage;

      const params = {
        page: this.adsPage,
        limit: ADS_PER_PAGE,
      };

      PostService.getAdsFromPost(this.id, params)
        .then(resp => {
          resp.forEach(ad => this.adsFromPost.push(ad));
          this.hasMoreAds = resp.length === ADS_PER_PAGE;

          if (this.hasMoreAds) {
            this.$nextTick(() => {
              this.initScrollPager('adsScrollTrigger', 'loadAds');
            });
          }

          return resp;
        })
        .catch(err => {
          this.hasMoreAds = false;
          this.error(err);
        })
        .finally(() => {
          this.isLoadingAds = false;
        });
    },
    onUpdated() {
      this.$nextTick(() => {
        if (this.improvedCopyInProgress) {
          this.initFetchTimer();
        }
      });

      this.newContentActuality = this.post.ai_content_actuality;
    },
    initFetchTimer() {
      if (this.fetchTimer !== null) {
        return;
      }

      this.fetchTimer = setTimeout(() => {
        this.fetchTimer = null;
        this.loadData(true);
      }, 10000);
    },
    destroyFetchTimer() {
      if (this.fetchTimer !== null) {
        clearTimeout(this.fetchTimer);
        this.fetchTimer = null;
      }
    },
    updateContentActuality() {
      if (this.isLoadingContantActualitySave) {
        return;
      }

      this.isLoadingContantActualitySave = true;

      const clonedEntity = new Post(this.post);
      clonedEntity.ai_content_actuality = this.newContentActuality;

      PostService.update(clonedEntity)
        .then(resp => {
          this.post.ai_content_actuality = resp.ai_content_actuality;
          this.post.ai_content_actuality_status = resp.ai_content_actuality_status;
          this.contectActualityFormOpen = false;

          this.showToast('success', this.tr('The changes have been successfully saved.'));

          return resp;
        })
        .catch(err => this.error(err))
        .finally(() => this.isLoadingContantActualitySave = false);
    },
    improveCopy() {
      this.post.improved_copy = new ImprovedCopy({ status: 1 });

      MagicToolService.improve({ postId: this.post.id })
        .then(resp => {
          this.post.improved_copy = resp;
          this.showToast('success', this.tr('We are already working on it! The evaluation will be completed within 1-2 minutes.'));
          this.initFetchTimer();

          return resp;
        })
        .catch(err => {
          this.post.improved_copy = null;
          this.error(err);
        });
    },
    editDateAndTime() {
      this.contectActualityFormOpen = true;
      if (!this.newContentActuality) {
        this.newContentActuality = new Date();
        this.newContentActuality.setSeconds(0);
        this.newContentActuality.setMilliseconds(0);
      }
    },
    cancelDateAndTimeEdit() {
      this.contectActualityFormOpen = false;
      this.newContentActuality = this.post.ai_content_actuality;
    },
    clearDateAndTime() {
      this.newContentActuality = null;
      this.updateContentActuality();
    },
    onMetricMenuOpen(open) {
      if (open) {
        this.tmpVisibleFields = [ ...this.metricVisibleFields ];
      }
      else {
        this.applyChangedFields();
      }
    },
    applyChangedFields() {
      this.tmpVisibleFields = this.tmpVisibleFields.sort((a, b) => {
        const indexA = this.metricFields.findIndex(m => m.machine_name === a.machine_name);
        const indexB = this.metricFields.findIndex(m => m.machine_name === b.machine_name);
        return indexA - indexB;
      });

      this.$store.commit('posts/setMetricVisibleFields', this.tmpVisibleFields);
    },
    isSelected(field) {
      return this.tmpVisibleFields.some(s => s.machine_name === field.machine_name);
    },
    toggleVisibleField(field) {
      if (this.isSelected(field)) {
        this.tmpVisibleFields = this.tmpVisibleFields.filter(i => i.machine_name !== field.machine_name);
      }
      else {
        this.tmpVisibleFields.push(field);
      }
    },
  },
};
</script>
